import React, { useCallback, useState } from "react";
import { Image } from "react-bootstrap";
import { useSelector } from "react-redux";
import _ from "lodash";

import Tooltip from "src/components/tooltip";
import { useUpdateUnreadForChatUserMutation } from "src/redux/actions/chatUsers";

import styles from "./chatUserRow.module.scss";
import cx from "classnames";
import LineEllipsis from "react-lines-ellipsis";

export interface ChatUserRowProps {
  detailView?: boolean;
  chatUser: Record<string, unknown>;
  name?: string;
  icon?: { thumbnail?: string } | null;
  global?: boolean;
  filter?: string;
  disabled?: boolean;
  activeChatUser?: string | number;
  // eslint-disable-next-line no-unused-vars
  onSelect?: (chatUser: Record<string, unknown>) => void;
  showLiveViewSlider?: boolean;
}

function renderName(chatUser: Record<string, unknown>) {
  return `${_.get(chatUser, "firstName") || ""} ${_.get(chatUser, "lastName") || ""}`.trim();
}

function formatLastOnline(timestamp: string | number | undefined): string {
  const value = timestamp != null ? Number(timestamp) : NaN;
  if (!Number.isFinite(value)) return "";

  const diffMs = Date.now() - value;
  if (diffMs < 0) return "";

  const diffMins = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? "minute" : "minutes"}`;
  if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? "hour" : "hours"}`;
  return "";
}

function renderInitials(chatUser: Record<string, unknown>) {
  if (chatUser.type === "visitor") {
    return <span className="paperflite-user-settings" />;
  }
  const firstName =
    _.get(chatUser, "firstName") != null
      ? String(_.get(chatUser, "firstName")).charAt(0)
      : "";
  const lastName =
    _.get(chatUser, "lastName") != null
      ? String(_.get(chatUser, "lastName")).charAt(0)
      : "";
  return (firstName + lastName).toUpperCase();
}

export function ChatUserRow({
  chatUser,
  icon,
  disabled,
  activeChatUser,
  onSelect,
  showLiveViewSlider
}: ChatUserRowProps) {

  const accountId = useSelector((state: any) => _.get(state, "accounts.item.id") as string | number | undefined);
  const updateUnreadMutation = useUpdateUnreadForChatUserMutation();

  const handleClick = useCallback(() => {
    if (disabled) return;
    const channelUuid =
      (_.get(chatUser, "uuid") as string | undefined) ??
      (accountId != null && (_.get(chatUser, "id") != null || _.get(chatUser, "chatUserId") != null)
        ? (chatUser as { type?: string }).type === "contact"
          ? `a_${accountId}.c_${_.get(chatUser, "id") ?? _.get(chatUser, "chatUserId")}`
          : `a_${accountId}.v_${_.get(chatUser, "id") ?? _.get(chatUser, "chatUserId")}`
        : null);
    if (channelUuid) {
      updateUnreadMutation.mutate(channelUuid);
    }
    onSelect?.(chatUser);
  }, [chatUser]);

  const [clamped, setClamped] = useState(false);

  const handleReflow = (state: { clamped: boolean }) => {
    setClamped(state.clamped);
  };

  const lastOnlineText = formatLastOnline(_.get(chatUser, "lastOnline") as string | number);

  return (
    <div
      className={cx(styles.chatUserRow, { [styles.disabled]: disabled, [styles.active]: activeChatUser === chatUser.id, [styles.showLiveViewSlider]: showLiveViewSlider })}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && handleClick()}
    >
      <div className={styles.userIconContainer}>
        {!icon && (
          <div className={styles.chatUserAvatars}>
            {renderInitials(chatUser)}
          </div>
        )}
        {icon && (
          <Image
            className={styles.chatUserIcon}
            src={icon.thumbnail}
            roundedCircle
          />
        )}
        {_.get(chatUser, "online") === true && (
          <span className={styles.onlineIndicator} />
        )}
      </div>
      
      <div className={styles.chatUserInfo}>
        <div className={cx(styles.chatUserName, {
          [styles.active]: activeChatUser === chatUser.id,
        })}>
          <Tooltip content={renderName(chatUser)} placement="top" disabled={!clamped}>
            <LineEllipsis
              text={renderName(chatUser)}
              maxLine="1"
              ellipsis="..."
              trimRight
              basedOn="words"
              onReflow={handleReflow}
            />
          </Tooltip>
        </div>
        {lastOnlineText ? (
          <div className={styles.lastOnline}>{lastOnlineText}</div>
        ) : null}
      </div>
    </div>
  );
}
